import { useState } from "react";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { IModels } from "../types/engine_model_type";

// アコーディオンメニューコンポーネントに渡すprops
interface EngineModelAccordionListProps {
  models: IModels;
  selectedModel: {
    modelName: string;
    modelNumber: string;
    dtcSearchData: string;
  };
  setSelectedModel: React.Dispatch<
    React.SetStateAction<{
      modelName: string;
      modelNumber: string;
      dtcSearchData: string;
      modelType: string;
    }>
  >;
  isLastList: boolean;
}

/**
 * アコーディオンメニューを表示するコンポーネント(リスト取得対応版)
 * @param models - 型式リスト
 * @param selectedModel - 選択された型式
 * @param setSelectedModel - 選択された型式を更新する関数
 * @param isLastList - 最後尾の型式リストかどうかを格納
 * @returns JSX.Element - アコーディオンメニューの表示
 */
export const EngineModelAccordionList: React.FC<
  EngineModelAccordionListProps
> = ({ models, selectedModel, setSelectedModel, isLastList }) => {
  const [open, setOpen] = useState(false); // メニューが開いているかどうかを格納
  const [open2, setOpen2] = useState<{
    [key: number]: boolean;
  }>({});

  // model2ラベル(3階層目)をレンダリングする関数
  const renderModel2Items = (
    model2_items: any[],
    parentIndex: number,
    isLastItems: boolean,
  ) => {
    if (!model2_items || model2_items.length === 0) {
      return null;
    }

    return model2_items.map((model2, idx) => {
      return (
        <div className="contents" key={model2.label}>
          <p
            className={`${
              !(isLastItems && idx === model2_items.length - 1) && "border-b"
            } ${
              idx === 0 && isLastItems && "border-t"
            } border-gray-700 cursor-pointer py-2 pr-4 pl-20 ${
              selectedModel.modelNumber === model2.label && "bg-sub-orange"
            }`}
            onClick={() =>
              setSelectedModel({
                modelName: models.model_group.model_items[parentIndex].label,
                modelNumber: model2.label,
                dtcSearchData: model2.dtc_search_data ?? "",
                modelType: "model2",
              })
            }
          >
            {model2.label}
          </p>
        </div>
      );
    });
  };

  // modelラベル(2階層目)をレンダリングする関数
  const renderModelItems = () => {
    const modelItemsArray = models.model_group.model_items;

    return modelItemsArray.map((model_items, index) => {
      const hasModel2Items =
        model_items.model2_items && model_items.model2_items.length > 0;
      const isSelected = selectedModel.modelNumber === model_items.label;
      const isLastItems = index === models.model_group.model_items.length - 1;

      return (
        <dd className="contents" key={model_items.label}>
          <div
            className={`flex items-center ${
              index + 1 !== models.model_group.model_items.length && "border-b"
            } border-gray-700 cursor-pointer py-2 pr-4 pl-12 ${
              isSelected && "bg-sub-orange"
            }`}
            onClick={() => {
              if (hasModel2Items) {
                setOpen2((prev) => ({
                  ...prev,
                  [index]: !prev[index],
                }));
              } else {
                setSelectedModel({
                  modelName: models.model_group.label,
                  modelNumber: model_items.label,
                  dtcSearchData: model_items.dtc_search_data ?? "",
                  modelType: "model",
                });
              }
            }}
          >
            {hasModel2Items && (
              <div className="pr-2">
                {open2[index] ? <FaMinus /> : <FaPlus />}
              </div>
            )}
            {model_items.label}
          </div>
          {hasModel2Items && open2[index] && (
            <div>
              {renderModel2Items(
                model_items.model2_items ?? [],
                index,
                isLastItems,
              )}
            </div>
          )}
        </dd>
      );
    });
  };

  // 代表型式ラベル(1層目)をレンダリングする関数
  const renderModelGroup = () => {
    return (
      <dt
        className={`flex items-center ${
          open && "border-b"
        }  border-gray-700 cursor-pointer py-2 px-3`}
        onClick={() => setOpen((prevState) => !prevState)}
      >
        <div className="pr-3">{open ? <FaMinus /> : <FaPlus />}</div>
        {models.model_group.label}
      </dt>
    );
  };

  // 各リスト部分をレンダリングする関数
  const renderList = () => {
    return (
      <div>
        {renderModelGroup()}
        {open && renderModelItems()}
      </div>
    );
  };

  return (
    <dl
      className={`bg-white rounded border border-gray-700 ${
        !isLastList && "mb-2"
      }`}
    >
      {renderList()}
    </dl>
  );
};
